import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { QHubBuddyLogo } from "@/components/QHubBranding";
import { NeuralBackground } from "@/components/NeuralBackground";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [form, setForm] = useState({ institution: "", name: "", role: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Error al enviar");
      }
      setSent(true);
    } catch (e) {
      setError(e.message || "No se pudo enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const isValid = form.institution && form.name && form.role && form.email && form.message;

  return (
    <div className="min-h-screen flex flex-col bg-white font-['Outfit']" data-testid="contact-page">
      <NeuralBackground />

      <header className="fixed top-0 left-0 right-0 z-50 bg-white/60 backdrop-blur-xl border-b border-gray-100/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <QHubBuddyLogo size="default" />
            </Link>
            <Link to="/" className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1.5" data-testid="back-to-landing">
              <ArrowLeft className="w-4 h-4" /> Volver al inicio
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center pt-24 pb-16 px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg"
        >
          {sent ? (
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-10 border border-gray-100 shadow-xl shadow-gray-200/40 text-center" data-testid="contact-success">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
              >
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Solicitud enviada</h2>
              <p className="text-gray-500 mb-8">Nos pondremos en contacto contigo pronto.</p>
              <Link to="/">
                <Button variant="outline" className="rounded-full px-6" data-testid="contact-back-home">
                  Volver al inicio
                </Button>
              </Link>
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-10 border border-gray-100 shadow-xl shadow-gray-200/40">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="contact-title">
                  Solicitar experiencia
                </h2>
                <p className="text-gray-500 text-sm">
                  Completa el formulario y te contactaremos para coordinar.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Institución</label>
                  <Input
                    value={form.institution}
                    onChange={(e) => update("institution", e.target.value)}
                    placeholder="Nombre de la institución"
                    className="h-12 rounded-xl"
                    data-testid="contact-institution"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Nombre</label>
                  <Input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Tu nombre completo"
                    className="h-12 rounded-xl"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Rol</label>
                  <Select value={form.role} onValueChange={(v) => update("role", v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="contact-role">
                      <SelectValue placeholder="Selecciona tu rol" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="director">Director/a</SelectItem>
                      <SelectItem value="coordinador">Coordinador/a Académico</SelectItem>
                      <SelectItem value="docente">Docente</SelectItem>
                      <SelectItem value="padre">Padre/Madre de Familia</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Email</label>
                  <Input
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="correo@ejemplo.com"
                    type="email"
                    className="h-12 rounded-xl"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Mensaje</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                    rows={4}
                    className="flex w-full rounded-xl border border-input bg-background px-3 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                    data-testid="contact-message"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 rounded-lg p-2.5" data-testid="contact-error">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={loading || !isValid}
                  className="w-full h-12 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-medium text-base flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:shadow-gray-900/20"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Enviando...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Enviar solicitud</>
                  )}
                </Button>
              </form>
            </div>
          )}
        </motion.div>
      </div>

      <footer className="relative z-10 py-6 px-4 text-center">
        <p className="text-gray-400 text-sm">QHUB.BUDDY — Red Neuronal de Aprendizaje</p>
      </footer>
    </div>
  );
};

export default ContactPage;
